import React from "react";
import katex from "katex";
import "katex/dist/katex.min.css";

// Texte inséré tel quel dans du HTML : toujours échappé.
const echapper = (texte = "") => String(texte).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// Les fractions pédagogiques doivent toujours être empilées avec une barre
// horizontale lisible, y compris à l'intérieur d'une formule en ligne.
export function normalizeDisplayFractions(latex = "") {
  let normalized = latex
    .replace(/√\(([^()]+)\)/g, "\\sqrt{$1}")
    .replace(/√\{([^{}]+)\}/g, "\\sqrt{$1}")
    .replace(/√([A-Za-z0-9]+)/g, "\\sqrt{$1}")
    .replace(/\\(?:t?frac)\b/g, "\\dfrac");

  // Les anciennes migrations contiennent souvent 1/2, x/100 ou
  // \sqrt2/2 dans une zone LaTeX. KaTeX les affiche avec une barre oblique.
  // On convertit uniquement les quotients de deux atomes mathématiques complets ;
  // sinon la barre est conservée : x_F/2, C_1C_2/(…), km/h, e^{-t/(RC)}…
  const atom = String.raw`(?:\\sqrt(?:\{[^{}]+\}|[A-Za-z0-9]+)|\\[A-Za-z]+|\([^()]+\)(?:\^\{?[-+A-Za-z0-9]+\}?)?|(?:[A-Za-z0-9]+|\{[^{}]+\})(?:\^\{?[-+A-Za-z0-9]+\}?)?)`;
  const slashFraction = new RegExp(`(${atom})\\s*\\/\\s*(${atom})`, "g");
  normalized = normalized.replace(slashFraction, (match, left, right, offset, whole) => {
    const before = whole[offset - 1] || "";
    const after = whole[offset + match.length] || "";
    const leftCut = /[_^}\\A-Za-z]/.test(before) || (/\d/.test(before) && !left.startsWith("\\"));
    const rightCut = /[_^{]/.test(after);
    if (leftCut || rightCut || !bracesBalanced(left) || !bracesBalanced(right)) return match;
    return `\\dfrac{${left}}{${right}}`;
  });
  return normalized;
}

function bracesBalanced(value) {
  let depth = 0;
  for (const character of value) {
    if (character === "{") depth += 1;
    else if (character === "}" && --depth < 0) return false;
  }
  return depth === 0;
}

function KatexSpan({ latex, display = false }) {
  let html = "";
  try {
    html = katex.renderToString(normalizeDisplayFractions(latex), {
      throwOnError: false,
      displayMode: display,
      output: "html",
    });
  } catch {
    html = `<span style="color:#dc2626">${echapper(latex)}</span>`;
  }
  return (
    <span
      className={display ? "ql-math-display" : "ql-math-inline"}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// Les liens des formules (\\href, \\url…) ne peuvent pas exécuter de code (javascript:).
const PROTOCOLES_AUTORISES = new Set(["http", "https", "mailto"]);
function liensSurs(contexte) {
  return !contexte.protocol || PROTOCOLES_AUTORISES.has(contexte.protocol);
}

// Rendu d'une ligne LaTeX unique
function KatexLine({ line }) {
  let html = "";
  try {
    html = katex.renderToString(normalizeDisplayFractions(line.trim()), {
      throwOnError: false,
      displayMode: true,
      output: "html",
      trust: liensSurs,
    });
  } catch {
    html = `<span style="font-family:monospace">${echapper(line)}</span>`;
  }
  return <span className="ql-formula-line" dangerouslySetInnerHTML={{ __html: html }} />;
}

// Rendu LaTeX brut (sortie MathLive sans délimiteurs) en mode display
// Chaque ligne est rendue séparément pour respecter les sauts de ligne
export function MathDisplay({ latex }) {
  if (!latex) return null;
  const lines = latex.split(/\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length === 0) return null;
  return (
    <span className="ql-formula-render">
      {lines.map((line, i) => <KatexLine key={i} line={line} />)}
    </span>
  );
}

// Les réponses de savoir-faire peuvent contenir une valeur numérique, du
// LaTeX brut issu du clavier mathématique, ou une réponse rédigée.
export function expectedAnswerLatex(value = "") {
  const source = String(value).trim();
  if (!source || /\$|\\[([]/.test(source)) return null;
  if (/\\[A-Za-z]+|√|[=^_]/.test(source)) return source;
  const numeric = source.match(/^([+-]?\d+(?:[.,]\d+)?(?:[eE][+-]?\d+)?(?:\s*\/\s*[+-]?\d+(?:[.,]\d+)?)?)(?:\s+([A-Za-zµμΩ°²³/^-]+))?$/);
  if (!numeric) return null;
  const number = numeric[1]
    .replace(/,/g, "{,}")
    .replace(/[eE]([+-]?\d+)/g, "\\times10^{$1}");
  return numeric[2] ? `${number}\\,\\mathrm{${numeric[2]}}` : number;
}

export function ExpectedAnswer({ value }) {
  if (!value && value !== 0) return null;
  const latex = expectedAnswerLatex(value);
  return latex === null ? <MathText text={String(value)} /> : <MathDisplay latex={latex} />;
}

// Splits text on math delimiters:
//   $$...$$ or \[...\]  → display math
//   $...$   or \(...\)  → inline math  (both from manual input and MathLive text mode)
export default function MathText({ text, raw = false }) {
  if (!text) return null;
  // raw=true : traiter tout le texte comme LaTeX display (sortie MathLive sans délimiteurs)
  if (raw) return <MathDisplay latex={text} />;
  const parts = [];
  const re = /\$\$([\s\S]*?)\$\$|\\\[([\s\S]*?)\\\]|\$([\s\S]*?)\$|\\\(([\s\S]*?)\\\)/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: "text", val: text.slice(last, m.index) });
    if      (m[1] !== undefined) parts.push({ type: "display", val: m[1] }); // $$...$$
    else if (m[2] !== undefined) parts.push({ type: "display", val: m[2] }); // \[...\]
    else if (m[3] !== undefined) parts.push({ type: "inline",  val: m[3] }); // $...$
    else                         parts.push({ type: "inline",  val: m[4] }); // \(...\)
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ type: "text", val: text.slice(last) });

  return (
    <>
      {parts.map((p, i) => {
        if (p.type === "text")    return <span key={i} translate="no" style={{ whiteSpace: "pre-wrap" }}>{p.val}</span>;
        if (p.type === "inline")  return <KatexSpan key={i} latex={p.val} />;
        if (p.type === "display") return <KatexSpan key={i} latex={p.val} display />;
        return null;
      })}
    </>
  );
}
